import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Clock, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useVerificationDialog } from "@/contexts/VerificationDialogContext";

export default function Contact() {
  const { openDialog } = useVerificationDialog();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1" data-testid="page-contact">
        {/* Hero band */}
        <section className="relative bg-slate-50/70 border-b border-slate-200">
          <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
          <div className="relative mx-auto max-w-5xl px-6 lg:px-10 py-16 lg:py-24">
            <Link
              to="/"
              data-testid="contact-back-link"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft size={14} /> Back to PS GLOBAL SERIES
            </Link>
            <div className="eyebrow mt-8">Contact</div>
            <h1
              data-testid="contact-page-title"
              className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900"
            >
              Get in <span className="font-semibold">touch.</span>
            </h1>
            <p className="mt-6 max-w-3xl text-[17px] text-slate-600 leading-relaxed">
              For sourcing inquiries, engagement scoping, supplier
              coordination or compliance questions, reach the PS GLOBAL
              SERIES, LLC office directly. We respond to verified business
              inquiries within one business day.
            </p>
          </div>
        </section>

        {/* Contact cards */}
        <section className="mx-auto max-w-5xl px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <ContactCard
              icon={Mail}
              label="Corporate Email"
              testId="contact-card-email"
              href="mailto:info@psglobalseries.com"
              value="info@psglobalseries.com"
              helper="Preferred channel for new engagements."
            />
            <ContactCard
              icon={Phone}
              label="Phone"
              testId="contact-card-phone"
              href="tel:+13395658894"
              value="+1 (339) 565-8894"
              helper="Monday – Friday · 9:00 AM – 6:00 PM ET."
            />
            <ContactCard
              icon={MapPin}
              label="Mailing Address"
              testId="contact-card-address"
              value={
                <>
                  PS GLOBAL SERIES, LLC
                  <br />
                  7345 W Sand Lake Rd, Ste 210, Office 7485
                  <br />
                  Orlando, FL 32819, United States
                </>
              }
              helper="Virtual office for client correspondence."
            />
            <ContactCard
              icon={ShieldCheck}
              label="State of Formation"
              testId="contact-card-state"
              value={
                <>
                  New Mexico, USA
                  <br />
                  Limited Liability Company
                </>
              }
              helper="Legal jurisdiction for all engagements is the State of New Mexico."
            />
            <ContactCard
              icon={Clock}
              label="Business Hours"
              testId="contact-card-hours"
              value={
                <>
                  Monday – Friday
                  <br />
                  9:00 AM – 6:00 PM Eastern Time (ET)
                </>
              }
              helper="Excludes US federal and state public holidays."
            />
            <ContactCard
              icon={ShieldCheck}
              label="Compliance / Privacy"
              testId="contact-card-compliance"
              href="mailto:info@psglobalseries.com?subject=Compliance%20Inquiry"
              value="info@psglobalseries.com"
              helper="Use the subject line “Compliance Inquiry”."
            />
          </div>

          {/* Inline CTA */}
          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-slate-200 pt-10">
            <div className="text-slate-700 text-[15px] max-w-xl">
              Ready to scope an engagement?{" "}
              <span className="font-semibold text-slate-900">
                Tell us what you need sourced
              </span>{" "}
              and a trade-liaison advisor will reach out.
            </div>
            <button
              type="button"
              onClick={openDialog}
              data-testid="contact-cta-sourcing"
              className="inline-flex items-center justify-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3.5 text-sm font-semibold tracking-tight rounded-sm transition-all shadow-sm hover:shadow-md"
            >
              Tell Us What You Need Sourced
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function ContactCard({ icon: Icon, label, value, helper, href, testId }) {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="h-11 w-11 inline-flex items-center justify-center bg-slate-900 text-white rounded-sm">
          <Icon size={20} />
        </div>
      </div>
      <div className="eyebrow mt-6">{label}</div>
      <div className="mt-2 font-display text-lg font-semibold text-slate-900 leading-snug break-words">
        {value}
      </div>
      {helper && (
        <p className="mt-3 text-[13px] text-slate-500 leading-relaxed">
          {helper}
        </p>
      )}
    </>
  );

  const baseCls =
    "group relative bg-white border border-slate-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.18)] hover:border-slate-300 block";

  return href ? (
    <a href={href} data-testid={testId} className={baseCls}>
      {inner}
    </a>
  ) : (
    <div data-testid={testId} className={baseCls}>
      {inner}
    </div>
  );
}
